/**
 * Toast notification system — custom Animated.View implementation.
 * Works reliably on both Expo native and Expo web (no Reanimated dependency).
 *
 * Usage:
 *   import { toast } from '../components/Toast'
 *   toast.success('Gemt!')
 *   toast.error('Noget gik galt')
 *   toast.info('Info besked')
 */
import { useEffect, useState, useRef, useCallback } from 'react'
import { Text, View, StyleSheet, Platform, Animated } from 'react-native'
import { CheckCircle, XCircle, Info } from 'lucide-react-native'
import { haptics } from '../utils/haptics'

type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  text: string
  type: ToastType
}

// Global emitter — outside React so toast() can be called from anywhere
let emitToast: ((item: ToastItem) => void) | null = null
let toastCounter = 0

export const toast = {
  success: (text: string) => {
    haptics.success()
    emitToast?.({ id: ++toastCounter, text, type: 'success' })
  },
  error: (text: string) => {
    haptics.error()
    emitToast?.({ id: ++toastCounter, text, type: 'error' })
  },
  info: (text: string) => {
    emitToast?.({ id: ++toastCounter, text, type: 'info' })
  },
}

export const globalAddToast = toast

function SingleToast({ item, onDone }: { item: ToastItem; onDone: () => void }) {
  const opacity = useRef(new Animated.Value(0)).current
  const translateY = useRef(new Animated.Value(20)).current

  const dismiss = useCallback(() => {
    Animated.timing(opacity, { toValue: 0, duration: 150, useNativeDriver: true }).start(() => onDone())
  }, [onDone, opacity])

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: 200, useNativeDriver: true }),
    ]).start()
    const t = setTimeout(dismiss, 3500)
    return () => clearTimeout(t)
  }, [])

  const iconColor = item.type === 'success' ? '#16a34a' : item.type === 'error' ? '#dc2626' : '#2563eb'
  const Icon = item.type === 'success' ? CheckCircle : item.type === 'error' ? XCircle : Info

  return (
    <Animated.View style={[styles.toast, { opacity, transform: [{ translateY }] }]}>
      <Icon size={16} color={iconColor} />
      <Text style={styles.toastText}>{item.text}</Text>
    </Animated.View>
  )
}

export default function ToastProvider() {
  const [items, setItems] = useState<ToastItem[]>([])

  useEffect(() => {
    emitToast = (item) => setItems(prev => [...prev, item])
    return () => { emitToast = null }
  }, [])

  const remove = (id: number) => setItems(prev => prev.filter(i => i.id !== id))

  if (items.length === 0) return null

  return (
    <View style={styles.container} pointerEvents="none">
      {items.map(item => (
        <SingleToast key={item.id} item={item} onDone={() => remove(item.id)} />
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? 'fixed' as any : 'absolute',
    bottom: Platform.OS === 'web' ? 24 : 100,
    left: 16,
    right: 16,
    alignItems: 'center',
    zIndex: 9999,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#1f2937',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginTop: 8,
    maxWidth: 360,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  toastText: {
    flex: 1,
    fontSize: 14,
    color: '#f9fafb',
    lineHeight: 20,
  },
})
